import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HeroSlideshow } from "@/components/HeroSlideshow";
import { ProductCard } from "@/components/ProductCard";
import { api } from "@/lib/api";
import { useSiteContent } from "@/context/SiteContentContext";
import { ArrowUpRight } from "lucide-react";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const { content } = useSiteContent();

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data));
  }, []);

  // Default fallbacks so the page never breaks if content hasn't loaded yet
  const c = content || {};
  const titleLines = c.hero_title_lines || ["Heirlooms,", "re-imagined", "for now."];
  const accentIdx = typeof c.hero_accent_line === "number" ? c.hero_accent_line : 1;
  const stats = c.hero_stats || [];
  const marquee = c.marquee_items || [];

  return (
    <>
      {/* Fixed background slideshow for the whole home page */}
      <HeroSlideshow slides={c.slides || []} intervalMs={c.slide_interval_ms || 5500} />

      <main className="relative z-10">
        {/* HERO */}
        <section className="relative">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 pb-24 lg:pt-24 lg:pb-36 grid lg:grid-cols-12 gap-10 items-center min-h-[78vh]">
            <div className="relative z-10 lg:col-span-8 xl:col-span-7">
              <div className="overline rise rise-1">{c.hero_overline || "Konokangen"}</div>
              <h1
                className="font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] leading-[1] tracking-tight font-normal mt-6 rise rise-2 text-[#0d0c0b]"
                style={{ textShadow: "0 1px 0 rgba(253,251,247,0.6)" }}
              >
                {titleLines.map((line, i) => (
                  <React.Fragment key={i}>
                    {i === accentIdx ? (
                      <em className="text-[#a8725e] not-italic font-semibold">{line}</em>
                    ) : (
                      line
                    )}
                    {i < titleLines.length - 1 && <br />}
                  </React.Fragment>
                ))}
              </h1>
              <p className="text-base lg:text-lg text-[#1a1918] mt-8 max-w-lg leading-relaxed rise rise-3 font-medium">
                {c.hero_subtitle}
              </p>
              <div className="mt-10 flex flex-wrap gap-4 rise rise-4">
                <Link to="/collection" className="btn-primary" data-testid="hero-cta-explore">
                  {c.hero_cta_primary || "Explore the collection"}
                  <ArrowUpRight size={16} />
                </Link>
                <Link to="/about" className="btn-ghost" data-testid="hero-cta-story">
                  {c.hero_cta_secondary || "Our story"}
                </Link>
              </div>
              {stats.length > 0 && (
                <div className="mt-16 grid grid-cols-3 gap-6 max-w-md rise rise-4">
                  {stats.map((s, i) => (
                    <div key={i}>
                      <div className="font-serif text-3xl text-[#0d0c0b] font-semibold">{s.value}</div>
                      <div className="text-[11px] tracking-[0.18em] uppercase text-[#3d3b38] mt-1 font-medium">
                        {s.label}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* MARQUEE */}
        {marquee.length > 0 && (
          <section className="border-y border-[#ebe5df] py-6 overflow-hidden bg-[#fdfbf7] backdrop-blur-md">
            <div className="marquee-track gap-16 font-serif text-2xl text-[#0d0c0b] font-semibold">
              {Array(2)
                .fill(0)
                .map((_, k) => (
                  <div key={k} className="flex gap-16 items-center px-8">
                    {marquee.map((t) => (
                      <span key={t + k} className="flex items-center gap-16 whitespace-nowrap">
                        {t}
                        <span className="w-2 h-2 rounded-full bg-[#c58b76]"></span>
                      </span>
                    ))}
                  </div>
                ))}
            </div>
          </section>
        )}

        {/* FEATURED */}
        <section className="bg-[#fdfbf7] backdrop-blur-md">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
            <div className="flex items-end justify-between mb-12">
              <div>
                <div className="overline">{c.featured_overline || "The atelier"}</div>
                <h2 className="font-serif text-4xl sm:text-5xl mt-3 text-[#0d0c0b]">
                  {c.featured_title || "Pieces we love this season"}
                </h2>
              </div>
              <Link
                to="/collection"
                className="hidden md:flex items-center gap-2 overline text-[#1a1918]"
              >
                View all <ArrowUpRight size={14} />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
              {featured.slice(0, 6).map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </div>
        </section>

        {/* STORY BAND */}
        <section className="bg-[#f3efea] backdrop-blur-md">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32 grid lg:grid-cols-2 gap-16 items-center">
            <div className="aspect-[4/5] overflow-hidden hover-zoom">
              <img
                src={c.story_image || ""}
                alt="Konokangen craftsmanship"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <div className="overline">{c.story_overline || "Craft"}</div>
              <h2 className="font-serif text-4xl sm:text-5xl mt-3 leading-[1.05]">
                {(c.story_title_lines || []).map((l, i) => (
                  <React.Fragment key={i}>
                    {l}
                    {i < (c.story_title_lines || []).length - 1 && <br />}
                  </React.Fragment>
                ))}
              </h2>
              <p className="text-[#5c5a56] mt-8 leading-relaxed max-w-lg">{c.story_body}</p>
              <Link to="/about" className="btn-ghost mt-10" data-testid="story-cta">
                {c.story_cta || "Read the journal"}
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
